import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from "primereact/toast";
import Loading from "../Components/Loading";
import ShareUrl from "../Pages/Products/ShareUrl";
import ShareQrPanel from "./ShareQrPanel";
import "../Styles/components/Share.scss";
import RoleAccess from "../Components/RoleAccess";
import { useSelector, useDispatch } from "react-redux";
import PlanGroupActions from "./../Redux/transition/plan-group/plan-group.reducer";

const SharePlan = (props) => {
  const enablingReport = useSelector((state) => state.planGroups.enabling);
  const successEnabling = useSelector((state) => state.planGroups.planReport);
  const errorEnabling = useSelector((state) => state.planGroups.errorEnabling);
  const business = useSelector((state) => state.businesses.business);
  const fetchingActivePlanGroups = useSelector((state) => state.planGroups.fetchingGraphedActive);

  const dispatch = useDispatch();
  const enablePlanReport = useCallback((planGroupId, report) => { dispatch(PlanGroupActions.enablePlanReportRequest(planGroupId, report)) }, [dispatch]);
  const getActivePlanGroups = useCallback((businessId) => { dispatch(PlanGroupActions.planGroupGraphedActiveRequest(businessId)) }, [dispatch]);

  const { t } = useTranslation("common");
  const { setSharePlanDialog, sharedPlan: { plan, planDescription, shortUrl, planGroupId } = {}, defMembership } = props;
  const toast = useRef(null);

  const businessPurpose = defMembership?.business.purpose;
  const purposeType = businessPurpose === "SERVICES" ? t("share.pay") : t("share.donate");

  // eslint-disable-next-line
  const [report, setReport] = useState("");
  // eslint-disable-next-line
  const [successReportable, setSuccessReportable] = useState(false);
  // eslint-disable-next-line
  const [successReportCode, setSuccessReportCode] = useState("");
  const [showToast, setShowToast] = useState(false);
  // eslint-disable-next-line
  const [reportPin, setReportPin] = useState("");
  const [enabling, setEnabling] = useState(false);
  // eslint-disable-next-line
  const [successToggle, setSuccessToggle] = useState(false);
  const [initialState, setInitialState] = useState(null);
  const [pinError, setPinError] = useState("");

  const [state, setState] = useState({
    check1: false,
    toggle: false,
    check2: false,
    pin: "",
    copiedFull: false,
    copiedUrl: false,
  });

  const [isEditingPin, setIsEditingPin] = useState(false);
  const [originalPin, setOriginalPin] = useState("");

  const isEnterpriseAccount = defMembership?.business?.enterprise;

  useEffect(() => {
    if (!enablingReport && successEnabling && enabling) {
      toast.current.show({
        life: 3000,
        severity: "success",
        summary: t("success"),
        detail: t("share.save_message"),
      });
    }
    if (!enablingReport && errorEnabling && enabling) {
      toast.current.show({
        life: 3000,
        severity: "error",
        summary: t("share.saved"),
        detail: t("share.save_message"),
      });
    }
    // eslint-disable-next-line
  }, [enabling, enablingReport, successEnabling]);

  const reportTexpart = <Trans
    i18nKey="share.report_text"
    values={{
      plan: plan,
      purposeType: purposeType
    }}
    components={{
      1: <span className="name-plan" />,
      2: <span className="name-plan" />,
      3: <span className="name-plan" />,
    }}>
  </Trans>

  const signupTextPart = < Trans
    i18nKey="share.signup_text"
    values={{
      plan: plan,
      purposeType: purposeType
    }
    }
    components={{
      1: <span className="name-plan" />,
      2: <span className="name-plan" />
    }
    }>
  </Trans >

  const messageToShow = (() => {
    let textPart = "";
    let hideAmountPart = "";
    let pinPartToShow = "";
    let pinPartToCopy = "";

    if (!showToast && props.reportable) {
      textPart = reportTexpart
    } else if (!showToast && !props.reportable) {
      textPart = signupTextPart
    } else if (showToast && successReportable) {
      textPart = reportTexpart
    } else {
      textPart = signupTextPart
    }

    if (!showToast && props.hideAmount) {
      hideAmountPart = t("share.toggle");
    }
    else if (showToast && successToggle) {
      hideAmountPart = t("share.toggle");
    }

    if (showToast && state.pin) {
      pinPartToCopy = `${t("share.pin")} ${state.pin}`;
    } else if (!showToast && props.reportCode) {
      pinPartToCopy = `${t("share.pin")}${props.reportCode}`;
    }

    if (showToast && state.pin) {
      pinPartToShow = (
        <p>
          {t("share.pin")} <span className="pincolor">{state.pin}</span>
        </p>
      );
    } else if (!showToast && props.reportCode) {
      pinPartToShow = (
        <p>
          {t("share.pin")} <span className="pincolor">{props.reportCode}</span>
        </p>
      );
    }

    const showText = (
      <div>
        <div className="d-flex flex-column gap-2">
          <span className="mb-3 text-black font-bold">{textPart}</span><br />

          {planDescription ? (<p className="description-text"> {t("share.desc", { planDescription })}</p>) : null}

          <p className="short-url-text">{t("share.url", { shortUrl })}</p>
        </div>

        {(pinPartToShow || hideAmountPart) && <div className="mt-3">
          {hideAmountPart && <div id="planDesc" className="mb-0">{hideAmountPart}</div>}
          {pinPartToShow && (
            <div className="text-black-500">{pinPartToShow}</div>
          )}
        </div>}
      </div>
    );
    const copyMessage = props.reportable
      ? t("share.report_text", {
        plan,
        purposeType
      })
      : t("share.signup_text", {
        plan,
        purposeType
      });

    const copyText = `${copyMessage}
${planDescription ? t("share.desc", { planDescription }) : ""}
${t("share.url", { shortUrl })}
${hideAmountPart ? `\n${hideAmountPart}` : ""}
${pinPartToCopy ? `\n${pinPartToCopy}` : ""}`;

    return { showText, copyText };
  })();

  useEffect(() => {
    if (props.visible) {
      const fetchInitialState = async () => {
        const response = {
          check1: props.reportable || false,
          toggle: props.hideAmount || false,
          check2: props.reportable && props.reportCode && props.reportCode !== "",
          pin: props.reportCode || "",
        };
        setInitialState(response);
        setState(response);
        setOriginalPin(response.pin);
        setIsEditingPin(false);
        setPinError("");
      };
      fetchInitialState();
    }
    // eslint-disable-next-line
  }, [props.visible]);

  // Reset dialog state when it closes
  useEffect(() => {
    if (!props.visible) {
      setShowToast(false);
      setIsEditingPin(false);
      setPinError("");
      setState({
        check1: false,
        toggle: false,
        check2: false,
        pin: "",
        copiedFull: false,
        copiedUrl: false,
      });
    }
  }, [props.visible]);

  const handleCheck1Change = (e) => {
    const newCheck1 = e.checked;
    setState((prevState) => ({
      ...prevState,
      check1: newCheck1,
      toggle: newCheck1 ? prevState.toggle : false,
      check2: false,
      pin: newCheck1 ? prevState.pin : "",
    }));

    // Reset pin editing state when disabling reportable
    if (!newCheck1) {
      setIsEditingPin(false);
      setPinError("");
    }
  };

  const handlePinChange = (e) => {
    const newPin = e.target.value;

    // Only allow digits
    if (/^\d*$/.test(newPin)) {
      setState((prevState) => ({
        ...prevState,
        pin: newPin,
      }));
    }

    // Validate pin length
    if (newPin.length > 0 && newPin.length < 5) {
      setPinError(t("share.pin_err"));
    } else {
      setPinError("");
    }
  };

  const handleKeyPress = (e) => {
    const char = String.fromCharCode(e.which);
    if (!/^\d$/.test(char)) {
      setPinError(t("share.pin_err"));
      e.preventDefault();
    } else {
      setPinError("");
    }
  };

  const handleToggleChange = (e) => {
    setState((prevState) => ({
      ...prevState,
      toggle: e.value,
    }));
  };

  const handleCheck2Change = (e) => {
    const isChecked = e.checked;

    setState((prevState) => ({
      ...prevState,
      check2: isChecked,
      pin: isChecked ? (originalPin || prevState.pin) : "",
    }));

    if (isChecked) {
      // If there's no original pin, start editing mode
      if (!originalPin) {
        setIsEditingPin(true);
      }
    } else {
      // When unchecking, stop editing and clear errors
      setIsEditingPin(false);
      setPinError("");
    }
  };

  // Check if current state differs from initial state 
  const isChanged = useMemo(() => {
    if (!initialState) return false;

    if (!state.check1 && !initialState.check1) {
      return false;
    }

    if (state.check1 !== initialState.check1) {
      return true;
    }

    if (state.check1 && initialState.check1) {
      if (state.toggle !== initialState.toggle) {
        return true;
      }

      if (state.check2 !== initialState.check2) {
        return true;
      }

      // Only compare pins if check2 is true in both states
      if (state.check2 && initialState.check2 && state.pin !== initialState.pin) {
        return true;
      }
    }

    return false;
  }, [state, initialState]);

  // Validate pin requirements
  const isPinValid = useMemo(() => {
    if (!state.check2) return true;
    return state.pin && state.pin.length === 5;
  }, [state.check2, state.pin]);

  // Determine if save button should be shown
  const shouldShowSave = useMemo(() => {
    return isChanged && isPinValid && (!state.check2 || (state.check2 && state.pin?.trim() !== ""));
  }, [isChanged, isPinValid, state.check2, state.pin]);

  const handleSave = () => {
    if (!isPinValid) return;

    enablePlanReport(props.sharedPlan.planGroupId, {
      reportable: state.check1,
      hideAmount: state.toggle,
      reportCode: state.pin,
    });

    setEnabling(true);
    setInitialState(state);
    setOriginalPin(state.pin);
    setIsEditingPin(false);
    setPinError("");
  };

  const handleEditPinToggle = () => {
    setIsEditingPin((prev) => {
      const newEditingState = !prev;
      if (!newEditingState) {
        // If canceling edit, revert to original pin
        setState((prevState) => ({
          ...prevState,
          pin: originalPin,
        }));
        setPinError("");
      }
      return newEditingState;
    });
  };

  const handleDialogClose = () => {
    if (!fetchingActivePlanGroups) {
      setSharePlanDialog(false);
      props.setDialogClosed(true);
      setShowToast(false);
      setIsEditingPin(false);
      setPinError("");
    }
  };

  const hasPin = reportPin?.toString().length === 5;

  return (
    <>
      <Toast
        ref={toast}
        onRemove={(message) => {
          if (message.severity === "success") {
            getActivePlanGroups(business.id);
            setEnabling(false);
            setShowToast(true);
            setSuccessReportable(state.check1);
            setSuccessToggle(state.toggle);
            setState((prevState) => ({
              ...prevState,
              copiedFull: false,
              copiedUrl: false,
            }));
          }
        }}
      />
      <>
        <div>
          {(isEnterpriseAccount) ?
            <ShareUrl
              visible={props.visible}
              sharedTier={{ plan, planDescription, shortUrl, planGroupId }}
              setShareDialog={handleDialogClose}
              headerFromShareProduct={t("product.share_url")}
            />
            :
            <Dialog
              header={t("product.share_url")}
              headerClassName="planShareHeader"
              className="planShare share_dialog"
              id="share_dialog"
              visible={props.visible}
              onHide={handleDialogClose}
            >
              {
                (enablingReport) ? <Loading /> :
                  <div>
                    <p className="share-title">{t("product.share_title")}</p>
                    <div className="share-content-with-qr copy-share">
                      <div className="share-link-column">
                        <div className="share-left-boxes">
                          <div className="share-url-top-box">
                            <div className="share-url-copy-row">
                              <p className="short-url-text">{shortUrl}</p>
                              <Button
                                icon={state.copiedUrl ? "pi pi-check" : "pi pi-copy"}
                                className="share-copy-url-btn share-corner-icon-btn p-button-text p-button-plain"
                                tooltip={state.copiedUrl ? t("share.copied") : t("share.copy_plan_link")}
                                tooltipOptions={{ position: "top" }}
                                aria-label={state.copiedUrl ? t("share.copied") : t("share.copy_plan_link")}
                                onClick={() => {
                                  navigator.clipboard.writeText(shortUrl);
                                  setState((prevState) => ({
                                    ...prevState,
                                    copiedUrl: true,
                                  }));
                                }}
                              />
                            </div>
                          </div>
                          <div className="productShare-box">
                            <div className="text-dis">
                              <div className="share-plan-title-row">
                                <span className="lead-text">{purposeType}</span>
                                <span className="name-plan">{plan}</span>
                              </div>
                              {planDescription ? (
                                <div className="description-text">{planDescription}</div>
                              ) : null}
                              <p className="short-url-text">{shortUrl}</p>
                            </div>
                          </div>
                        </div>
                        <Button
                          label={state.copiedFull ? t("share.copied") : t("share.copy")}
                          icon="pi pi-copy"
                          className="copy_btn share-copy-full-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(messageToShow.copyText);
                            setState((prevState) => ({
                              ...prevState,
                              copiedFull: true,
                              change_pin: true,
                            }));
                          }}
                        />
                      </div>
                      <ShareQrPanel
                        visible={props.visible}
                        entityType="planGroup"
                        entityId={planGroupId}
                        fileName={`${plan || "product"}-qr.png`}
                      />
                    </div>
                    <RoleAccess roles={["OWNER", "ADMIN"]}>
                      <p className="share-settings-title">{t("share.share_settings")}</p>
                      <div className="grid mt-4 mb-4">
                        <span className="col-4">
                          <Checkbox
                            inputId="isReport"
                            checked={state.check1}
                            onChange={handleCheck1Change}
                          />
                          <label htmlFor="isReport" className="p-checkbox-label">{t("share.enable_report")} </label>
                          <span className="p-checkbox-hint hint-style">{t("share.enable_report_hint")} </span>
                        </span>
                        <div className="col-8">
                          <span className="main-hint">{t("share.enable_report_hint_main")} </span>
                        </div>
                        <br />
                      </div>

                      <div className="grid mb-4">
                        <span className="col-4">
                          <InputSwitch
                            inputId="toggle"
                            checked={state.toggle}
                            onChange={handleToggleChange}
                            disabled={!state.check1}
                          />
                          <label htmlFor="toggle" className="ml-3">{t("share.toggle_label")}</label>
                        </span>
                        <span className="col-8">
                          <span className="main-hint">{t("share.toggle_hint_main")} </span>
                        </span>
                      </div>

                      <div className="grid nested-grid">
                        <div className="col-4">
                          <div className="grid">
                            <span className="col-12">
                              <Checkbox
                                inputId="addPin"
                                checked={state.check2}
                                onChange={handleCheck2Change}
                                disabled={!state.check1}
                              />
                              <label htmlFor="addPin" className="p-checkbox-label"> {t("share.add_pin")}  </label>
                            </span>
                            <div className="col-12">
                              <span className="flex flex-column">
                                {state.check2 && (
                                  <InputText
                                    id="pin"
                                    value={state.pin}
                                    onChange={handlePinChange}
                                    onKeyPress={handleKeyPress}
                                    placeholder="12345"
                                    disabled={!isEditingPin}
                                    className={`p-inputtext ${!isEditingPin && originalPin ? "borderless" : ""}`}
                                    maxLength={5}
                                  />
                                )}
                                {(pinError && state.check2) && <small className="p-error mt-1">{pinError}</small>}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-8">
                          <div className="grid">
                            <span className="col-12 main-hint">
                              {t("share.add_pin_hint_main")}
                            </span>
                            <span className="col-12 text-left">
                              {originalPin && state.check2 && (
                                <Button
                                  label={isEditingPin ? t("cancel") : t("change_pin")}
                                  className="p-button-text pl-0 pb-0"
                                  onClick={handleEditPinToggle}
                                />
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-content-end align-items-end pin-box">
                        {shouldShowSave && (
                          <Button
                            label={t("share.save")}
                            onClick={handleSave}
                            loading={enablingReport}
                            className="save_btn"
                            disabled={!isPinValid}
                          />
                        )}

                        {report && reportPin && !hasPin && (
                          <small className="p-error">{t("share.pin_err")}</small>
                        )}
                      </div>
                    </RoleAccess>
                  </div>
              }
            </Dialog>}
        </div>
      </>
    </>
  );
};
export default SharePlan;
